import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, CalendarDays, UserPlus } from "lucide-react";
import { api, errText } from "../../lib/admin";
import { Btn, Empty, Loading, Pill, FilterBar, field, fmtDate, fmtDateTime } from "./ui";
import { SLOTS, slotOf, slotFromHour } from "./CalendarAdmin";

const STATUS = { pending: ["Da confermare", "gold"], confirmed: ["Confermata", "green"], cancelled: ["Annullata", "red"] };

const waLink = (phone, name) => {
  let d = (phone || "").replace(/\D/g, "");
  if (d.length === 10) d = `39${d}`;
  return `https://wa.me/${d}?text=${encodeURIComponent(`Ciao ${name.split(" ")[0]}, sono Gioia del Centro Estetico.`)}`;
};

const DateEditor = ({ a, confirm, onSave, onClose }) => {
  const [d, setD] = useState(a.preferred_date || "");
  const [t, setT] = useState(slotOf(a.preferred_time) === "Da definire" ? "" : slotOf(a.preferred_time));
  const [h, setH] = useState(a.confirmed_time || "");
  const save = () => {
    if (confirm && !d) { toast.error("Scegli la data dell'appuntamento"); return; }
    onSave(a, { preferred_date: d, preferred_time: h ? slotFromHour(h) : t, confirmed_time: h, ...(confirm ? { status: "confirmed" } : {}) });
  };
  return (
    <div className="mt-4 bg-[#F6F2EC] rounded-lg p-3 flex flex-wrap items-end gap-3" data-testid={`appt-date-editor-${a.id}`}>
      {confirm && <p className="w-full text-sm text-[#14110F]">Fissa giorno e ora per confermare:</p>}
      <label className="text-xs text-[#6b625a]">Data<input type="date" value={d} onChange={(e) => setD(e.target.value)} className={`${field} mt-1 py-2`} data-testid={`appt-date-input-${a.id}`} /></label>
      <label className="text-xs text-[#6b625a]">Ora esatta<input type="time" step={900} value={h} onChange={(e) => setH(e.target.value)} className={`${field} mt-1 py-2`} data-testid={`appt-time-input-${a.id}`} /></label>
      {!h && <label className="text-xs text-[#6b625a]">oppure fascia<select value={t} onChange={(e) => setT(e.target.value)} className={`${field} mt-1 py-2`} data-testid={`appt-slot-select-${a.id}`}>
        <option value="">Da definire</option>{SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select></label>}
      <Btn onClick={save} data-testid={`appt-date-save-${a.id}`}>{confirm ? "Conferma appuntamento" : "Salva"}</Btn>
      <Btn variant="ghost" onClick={onClose}>Chiudi</Btn>
    </div>
  );
};

const Card = ({ a, onStatus, onPatch, busy }) => {
  const [label, tone] = STATUS[a.status] || STATUS.pending;
  const [editDate, setEditDate] = useState("");
  return (
    <article data-testid={`appt-card-${a.id}`} className="bg-white border border-[#e7dfd3] rounded-xl p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-serif-d text-2xl font-light text-[#14110F] leading-tight">{a.name}</h3>
          <p className="mt-1 text-[#8A6A3A]">{a.service}</p>
        </div>
        <Pill tone={tone} testId={`appt-status-${a.id}`}>{label}</Pill>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-[#4f4841]">
        <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#8A6A3A]" /> {a.preferred_date ? fmtDate(a.preferred_date) : "Data da concordare"}{a.confirmed_time ? ` · ore ${a.confirmed_time}` : a.preferred_time ? ` · ${a.preferred_time}` : ""}</p>
        <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#8A6A3A]" /> <a className="underline underline-offset-4" href={`tel:${a.phone}`}>{a.phone}</a></p>
        {a.email && <p className="flex items-center gap-2 break-all"><Mail className="w-4 h-4 shrink-0 text-[#8A6A3A]" /> {a.email}</p>}
        {a.referrer_name && <p className="flex items-center gap-2"><UserPlus className="w-4 h-4 text-[#8A6A3A]" /> Invitata da {a.referrer_name}</p>}
      </div>
      {a.notes && <p className="mt-3 text-sm text-[#4f4841] bg-[#F6F2EC] rounded-lg p-3 whitespace-pre-line">{a.notes}</p>}
      {editDate && <DateEditor a={a} confirm={editDate === "confirm"} onClose={() => setEditDate("")} onSave={async (x, upd) => { if (await onPatch(x, upd)) setEditDate(""); }} />}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        {a.status !== "confirmed" && <Btn variant="dark" loading={busy} onClick={() => setEditDate("confirm")} data-testid={`appt-confirm-${a.id}`}>Conferma</Btn>}
        {a.status !== "cancelled" && <Btn variant="danger" loading={busy} onClick={() => onStatus(a, "cancelled")} data-testid={`appt-cancel-${a.id}`}>Annulla</Btn>}
        {a.status !== "pending" && <Btn variant="ghost" loading={busy} onClick={() => onStatus(a, "pending")} data-testid={`appt-reset-${a.id}`}>Rimetti in attesa</Btn>}
        {!editDate && <Btn variant="ghost" onClick={() => setEditDate("edit")} data-testid={`appt-edit-date-${a.id}`}>Modifica data e ora</Btn>}
        <a href={waLink(a.phone, a.name)} target="_blank" rel="noreferrer" data-testid={`appt-wa-${a.id}`} className="inline-flex items-center gap-1.5 text-[0.72rem] tracking-[0.14em] uppercase text-[#2f6b3d] px-2 py-2 hover:underline">
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
        <span className="ml-auto text-xs text-[#6b625a]">Ricevuta {fmtDateTime(a.created_at)}</span>
      </div>
    </article>
  );
};

export const AppointmentsAdmin = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/admin/appointments").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(errText(e)); setItems([]); });
  }, []);
  const onStatus = async (a, status) => {
    setBusy(a.id);
    try {
      await api.patch(`/admin/appointments/${a.id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === a.id ? { ...x, status } : x)));
      toast.success(`Prenotazione di ${a.name}: ${STATUS[status][0].toLowerCase()}`);
    } catch (e) { toast.error(errText(e)); }
    setBusy("");
  };
  const onPatch = async (a, upd) => {
    try {
      await api.patch(`/admin/appointments/${a.id}`, upd);
      setItems((xs) => xs.map((x) => (x.id === a.id ? { ...x, ...upd } : x)));
      toast.success(upd.status === "confirmed" ? `Appuntamento di ${a.name} confermato` : "Data aggiornata");
      return true;
    } catch (e) { toast.error(errText(e)); return false; }
  };
  if (!items) return <Loading />;
  const count = (k) => items.filter((x) => (x.status || "pending") === k).length;
  const shown = filter === "all" ? items : items.filter((x) => (x.status || "pending") === filter);
  return (
    <div className="space-y-6" data-testid="admin-appointments">
      <FilterBar value={filter} onChange={setFilter} testPrefix="appt-filter" options={[["pending", "Da confermare", count("pending")], ["confirmed", "Confermate", count("confirmed")], ["cancelled", "Annullate", count("cancelled")], ["all", "Tutte", items.length]]} />
      {shown.length ? <div className="grid xl:grid-cols-2 gap-4">{shown.map((a) => <Card key={a.id} a={a} onStatus={onStatus} onPatch={onPatch} busy={busy === a.id} />)}</div>
        : <Empty testId="appt-empty">Nessuna prenotazione in questa sezione.</Empty>}
    </div>
  );
};
